<script lang="ts">
  import { ApiError, apiGet } from '../../lib/api';
  import {
    clampRadius,
    edgeStyle,
    nearbyQuery,
    RADIUS_MAX_M,
    RADIUS_MIN_M,
    vertexStyle,
  } from '../../lib/debug-graph';
  import { parseCoord, type Coord } from '../../lib/debug-walk';
  import { geoJsonToString, nearbyToGeoJson } from '../../lib/geojson';
  import type { NearbyEdge, NearbyResponse, NearbyVertex } from '../../lib/nearby-types';
  import { withUniqueKeys } from '../../lib/unique-keys';

  /** Debug-graph page's local store, kept in one rune-tracked object. */
  interface DebugGraphState {
    /** Centre coord as typed, parsed on every Query click. */
    centreText: string;
    /** Radius (metres) — clamped into `[RADIUS_MIN_M, RADIUS_MAX_M]` before submission. */
    radiusM: number;
    /** Most recent /v1/debug/nearby response. */
    response: NearbyResponse | undefined;
    /** Vertex the user last clicked in the sidebar list. */
    selected: NearbyVertex | undefined;
    /** Current lifecycle phase surfaced to the button + banner. */
    status: 'idle' | 'loading' | 'ready' | 'error';
    /** Human-readable error message shown when `status === 'error'`. */
    errorMessage: string;
  }

  /** Alexanderplatz (public transit hub, not a residential address). */
  const DEFAULT_CENTRE = '52.52200,13.41300';

  const state = $state<DebugGraphState>({
    centreText: DEFAULT_CENTRE,
    radiusM: 300,
    response: undefined,
    selected: undefined,
    status: 'idle',
    errorMessage: '',
  });

  const vertexCount = $derived(state.response?.vertices.length ?? 0);
  const selectedJson = $derived(state.selected ? JSON.stringify(state.selected, null, 2) : '');

  /** Loads the nearby response for `centre`/`radius` into `state`, clearing any prior selection. */
  async function loadNearby(centre: Coord, radius: number): Promise<void> {
    const response = await apiGet<NearbyResponse>('/v1/debug/nearby', nearbyQuery(centre, radius));
    state.response = response;
    state.selected = undefined;
    state.status = 'ready';
  }

  /** Flips into error state with either an `ApiError.message` or a generic fallback. */
  function reportError(error: unknown): void {
    state.errorMessage = error instanceof ApiError ? error.message : 'Request failed.';
    state.status = 'error';
  }

  /** Flips into error state with the "bad centre coord" message. */
  function reportInvalidCentre(): void {
    state.errorMessage = 'Enter the centre as "lat,lon".';
    state.status = 'error';
  }

  /** Wraps `loadNearby` with the loading marker and error handler. */
  async function attemptFetch(centre: Coord, radius: number): Promise<void> {
    state.status = 'loading';
    try {
      await loadNearby(centre, radius);
    } catch (error: unknown) {
      reportError(error);
    }
  }

  /** Parses the centre input, clamps the radius, and dispatches to `attemptFetch`. */
  async function fetchNearby(): Promise<void> {
    const centre = parseCoord(state.centreText);
    if (!centre) {
      reportInvalidCentre();
      return;
    }
    await attemptFetch(centre, clampRadius(state.radiusM));
  }

  /** Records `vertex` as the sidebar's selection so its JSON renders in the panel. */
  function selectVertex(vertex: NearbyVertex): void {
    state.selected = vertex;
  }

  /** Encodes the current nearby response as a browser-triggered `.geojson` download. */
  function triggerDownload(): void {
    if (!state.response) return;
    const collection = nearbyToGeoJson(state.response);
    const blob = new Blob([geoJsonToString(collection)], { type: 'application/geo+json' });
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = 'nearby.geojson';
    anchor.click();
    URL.revokeObjectURL(blobUrl);
  }

  /** Comma-joined vertex coordinate. */
  function vertexKey(vertex: NearbyVertex): string {
    return `${String(vertex.coord[0])},${String(vertex.coord[1])}`;
  }

  /** Vertices paired with list-unique keys; two vertices can sit on one coord. */
  const keyedVertices = $derived(
    state.response ? withUniqueKeys(state.response.vertices, vertexKey) : [],
  );

  /** Pre-formatted vertex row so the template interpolates one string. */
  function vertexRowLine(vertex: NearbyVertex): string {
    const style = vertexStyle(vertex);
    return `${style.label} · ${vertex.distance_m.toFixed(1)} m · ${String(vertex.edges.length)} edges`;
  }

  /** Pre-formatted edge row so the template interpolates one string. */
  function edgeRowLine(edge: NearbyEdge): string {
    const style = edgeStyle(edge);
    return `${style.label} · ${String(edge.cost_ms)} ms`;
  }

  /** Pre-formatted vertex-count line for the header. */
  const countLine = $derived(`Vertices: ${String(vertexCount)}`);

  /** Pre-formatted radius-slider label so its `{...}` interpolation is a single string. */
  const radiusLabel = $derived(`Radius (m): ${String(state.radiusM)}`);
</script>

<!-- Developer debug page: /v1/debug/nearby dump viewer. Not password-gated — reach
     it via /#/debug/graph after opening the app in a fresh tab. -->
<section class="debug-graph">
  <header class="head">
    <h1>Graph inspector</h1>
    <p class="hint">Query walking-graph vertices around a coord (Alexanderplatz preloaded).</p>
  </header>
  <div class="controls">
    <label class="field">
      <span>Centre (lat,lon)</span>
      <input type="text" bind:value={state.centreText} aria-label="Centre coord" />
    </label>
    <label class="field">
      <span>{radiusLabel}</span>
      <input
        type="range"
        min={RADIUS_MIN_M}
        max={RADIUS_MAX_M}
        step="50"
        bind:value={state.radiusM}
        aria-label="Radius metres"
      />
    </label>
    <button
      type="button"
      onclick={() => {
        void fetchNearby();
      }}
      disabled={state.status === 'loading'}
    >
      {state.status === 'loading' ? 'Loading…' : 'Query'}
    </button>
    <button type="button" onclick={triggerDownload}>Download GeoJSON</button>
  </div>
  {#if state.status === 'error'}
    <p class="error" role="alert">{state.errorMessage}</p>
  {/if}
  <p class="count">{countLine}</p>
  <div class="body">
    <aside class="list" aria-label="Vertex list">
      {#if state.response}
        <ol>
          {#each keyedVertices as entry (entry.key)}
            <li>
              <button
                type="button"
                class="row"
                style:border-left-color={vertexStyle(entry.item).color}
                onclick={() => {
                  selectVertex(entry.item);
                }}
              >
                {vertexRowLine(entry.item)}
              </button>
              {#if entry.item.edges.length > 0}
                <ul class="edges">
                  {#each entry.item.edges as edge, edgeIndex (edgeIndex)}
                    <li style:border-left-color={edgeStyle(edge).color}>{edgeRowLine(edge)}</li>
                  {/each}
                </ul>
              {/if}
            </li>
          {/each}
        </ol>
      {/if}
    </aside>
    <aside class="detail" aria-label="Selected vertex JSON">
      {#if selectedJson}
        <pre>{selectedJson}</pre>
      {:else}
        <p class="empty">Click a vertex on the left to inspect it.</p>
      {/if}
    </aside>
  </div>
</section>

<style>
  .debug-graph {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    height: 100vh;
    box-sizing: border-box;
    font-family: system-ui, sans-serif;
  }

  .head h1 {
    margin: 0;
    font-size: 18px;
  }

  .hint {
    margin: 4px 0 0;
    font-size: 12px;
    color: #6b6b6b;
  }

  .controls {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: end;
  }

  .field {
    display: flex;
    flex-direction: column;
    font-size: 12px;
  }

  .field input[type='text'] {
    padding: 4px 6px;
    font-family: monospace;
  }

  .error {
    color: #a01313;
    margin: 0;
    font-size: 13px;
  }

  .count {
    margin: 0;
    font-size: 13px;
    color: #4b4b4b;
  }

  .body {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    flex: 1;
    min-height: 0;
  }

  .list,
  .detail {
    overflow: auto;
    background: #f5f5f5;
    padding: 8px;
    border-radius: 4px;
    font-size: 12px;
  }

  .list ol {
    margin: 0;
    padding-left: 20px;
  }

  .row {
    display: inline-block;
    background: none;
    border: none;
    border-left: 4px solid transparent;
    padding: 2px 6px;
    cursor: pointer;
    font: inherit;
    text-align: left;
  }

  .row:hover {
    text-decoration: underline;
  }

  .edges {
    list-style: none;
    padding-left: 16px;
    margin: 2px 0;
  }

  .edges li {
    border-left: 4px solid transparent;
    padding-left: 6px;
  }

  .detail pre {
    margin: 0;
    font-size: 11px;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .empty {
    color: #6b6b6b;
    font-style: italic;
    margin: 0;
  }
</style>
